import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Typography,
  Box,
  LinearProgress,
  Paper,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { uploadMedia } from "../services/uploadService";
import axios from "../api/api";
import { tourFormConfig } from "../config/formConfig";
import { mapApiToFormik } from "../utils/mapApiToFormik";
import { getPackageById, updatePackage } from "../api/packageApi";

import BasicInfoSection from "./BasicInfoSection";
import ItinerarySection from "./ItinerarySection";
import MediaUploadSection from "./MediaUploadSection";
import AvailabilitySection from "./AvailabilitySection";
import AdditionalInfoSection from "./AdditionalInfoSection";
import ConfirmationDialog from "../../../shared/ui/ConfirmationDialog";
import PricingSection from "./PricingSection";

const BRAND = "#E50000";

const TourPackageForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [initialValues, setInitialValues] = useState(
    tourFormConfig.initialValues,
  );
  const [loading, setLoading] = useState(isEdit);
  const [uploading, setUploading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [confirmBackOpen, setConfirmBackOpen] = useState(false);

  const steps = [
    "Basic Info",
    "Itinerary",
    "Pricing",
    "Availability",
    "Media",
    "Additional Info",
  ];
  const [activeStep, setActiveStep] = useState(0);

  const handleMediaUpload = async (files, setProgress) => {
    return await uploadMedia(files, setProgress);
  };

  const handleSubmit = async (values) => {
    setFormValues(values);
    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setConfirmOpen(false);
    try {
      toast.info(isEdit ? "Updating package..." : "Submitting package...");
      isEdit
        ? await updatePackage(id, formValues)
        : await axios.post("/api/packages", formValues);
      toast.success(
        isEdit
          ? "Tour package updated successfully!"
          : "Tour package created successfully!",
      );
      navigate("/tours");
    } catch (err) {
      const message =
        err?.response?.data?.message || "Submission failed. Please try again.";
      toast.error(message);
      console.error(err);
    }
  };

  useEffect(() => {
    if (isEdit) {
      getPackageById(id)
        .then((res) => {
          const formatted = mapApiToFormik(res.data);
          setInitialValues(formatted);
        })
        .catch((err) => {
          toast.error("Failed to load package");
          console.error(err);
        })
        .finally(() => setLoading(false));
    }
  }, [id]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: BRAND }} size={36} thickness={4} />
        <Typography sx={{ color: "#6B7280", fontSize: "0.875rem" }}>
          Loading package data...
        </Typography>
      </Box>
    );
  }

  const progressPct = (activeStep / (steps.length - 1)) * 100;

  return (
    <Box
      sx={{
        bgcolor: "#F7F8FC",
        minHeight: "100vh",
        py: { xs: 3, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {uploading && (
        <LinearProgress
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            zIndex: 9999,
            "& .MuiLinearProgress-bar": { bgcolor: BRAND },
          }}
        />
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={tourFormConfig.validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, dirty, validateForm, setTouched }) => {
          const stepSectionKeys = [
            "basicInfo",
            "itinerary",
            "pricing",
            "availability",
            "media",
            "additional",
          ];
          const touchAll = (o) => {
            if (typeof o !== "object" || o === null) return true;
            if (Array.isArray(o)) return o.map(touchAll);
            return Object.fromEntries(
              Object.entries(o).map(([k, v]) => [k, touchAll(v)]),
            );
          };
          const handleNext = async () => {
            const errs = await validateForm();
            const key = stepSectionKeys[activeStep];
            if (errs[key]) {
              setTouched({ [key]: touchAll(errs[key]) }, false);
              return;
            }
            if (activeStep === steps.length - 1) {
              handleSubmit(values);
            } else {
              setActiveStep((prev) => prev + 1);
            }
          };
          return (
            <>
              {/* ── Page header ── */}
              <Box sx={{ mb: 3 }}>
                <Button
                  startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />}
                  onClick={() =>
                    dirty ? setConfirmBackOpen(true) : navigate(-1)
                  }
                  disableRipple
                  sx={{
                    mb: 2,
                    color: "#6B7280",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    textTransform: "none",
                    px: 0,
                    minWidth: 0,
                    "&:hover": { bgcolor: "transparent", color: "#374151" },
                  }}
                >
                  Back to Tours
                </Button>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: "#111827",
                        lineHeight: 1.25,
                        fontSize: { xs: "1.25rem", md: "1.45rem" },
                      }}
                    >
                      {isEdit ? "Edit Tour Package" : "Create Tour Package"}
                    </Typography>
                    <Typography
                      sx={{ color: "#6B7280", fontSize: "0.84rem", mt: 0.3 }}
                    >
                      {isEdit
                        ? "Update the details of your existing tour"
                        : "Fill in the steps below to publish a new tour"}
                    </Typography>
                  </Box>

                  <Chip
                    label={`Step ${activeStep + 1} / ${steps.length}`}
                    size="small"
                    sx={{
                      alignSelf: "center",
                      bgcolor: "rgba(229,0,0,0.08)",
                      color: BRAND,
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 26,
                      borderRadius: 1,
                    }}
                  />
                </Box>
              </Box>

              {/* ── Card ── */}
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid #E5E7EB",
                  overflow: "hidden",
                  bgcolor: "#fff",
                  boxShadow:
                    "0 1px 3px rgba(0,0,0,0.06), 0 6px 24px rgba(0,0,0,0.04)",
                }}
              >
                {/* Progress stripe */}
                <Box sx={{ height: 3, bgcolor: "#F3F4F6" }}>
                  <Box
                    sx={{
                      height: "100%",
                      width: `${progressPct}%`,
                      bgcolor: BRAND,
                      transition: "width 0.35s ease",
                      borderRadius: "0 2px 2px 0",
                    }}
                  />
                </Box>

                <Form>
                  {/* Section body */}
                  <Box sx={{ px: { xs: 2, sm: 4 }, py: 4, minHeight: 440 }}>
                    {activeStep === 0 && <BasicInfoSection />}
                    {activeStep === 1 && <ItinerarySection />}
                    {activeStep === 2 && <PricingSection />}
                    {activeStep === 3 && <AvailabilitySection />}
                    {activeStep === 4 && (
                      <MediaUploadSection
                        handleMediaUpload={handleMediaUpload}
                      />
                    )}
                    {activeStep === 5 && <AdditionalInfoSection />}
                  </Box>

                  {/* Footer */}
                  <Box
                    sx={{
                      px: { xs: 2, sm: 4 },
                      py: 2.75,
                      bgcolor: "#FAFAFA",
                      borderTop: "1px solid #F3F4F6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Button
                      disabled={activeStep === 0}
                      onClick={() => setActiveStep((prev) => prev - 1)}
                      variant="outlined"
                      startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
                      sx={{
                        textTransform: "none",
                        borderColor: "#D1D5DB",
                        color: "#374151",
                        fontWeight: 500,
                        fontSize: "0.85rem",
                        borderRadius: 1.5,
                        px: 2.5,
                        py: 0.9,
                        "&:hover": {
                          borderColor: "#9CA3AF",
                          bgcolor: "#F9FAFB",
                        },
                        "&.Mui-disabled": {
                          borderColor: "#F3F4F6",
                          color: "#D1D5DB",
                        },
                      }}
                    >
                      Previous
                    </Button>

                    {/* Dot indicator */}
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      {steps.map((_, i) => (
                        <Box
                          key={i}
                          sx={{
                            width: i === activeStep ? 18 : 6,
                            height: 6,
                            borderRadius: 3,
                            bgcolor:
                              i === activeStep
                                ? BRAND
                                : i < activeStep
                                  ? "rgba(229,0,0,0.28)"
                                  : "#E5E7EB",
                            transition: "all 0.3s ease",
                          }}
                        />
                      ))}
                    </Box>

                    <Button
                      variant="contained"
                      disableElevation
                      onClick={handleNext}
                      sx={{
                        textTransform: "none",
                        bgcolor: BRAND,
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        borderRadius: 1.5,
                        px: 3,
                        py: 0.9,
                        "&:hover": {
                          bgcolor: "#C40000",
                          boxShadow: "0 3px 10px rgba(229,0,0,0.28)",
                        },
                      }}
                    >
                      {activeStep === steps.length - 1
                        ? "Submit Package"
                        : "Continue"}
                    </Button>
                  </Box>
                </Form>
              </Paper>

              <ConfirmationDialog
                open={confirmOpen}
                onCancel={() => setConfirmOpen(false)}
                onConfirm={handleConfirmSubmit}
                title="Confirm Submission"
                description="Are you sure you want to submit this tour package? This action will save it to the database."
                confirmText="Yes, Submit"
                confirmColor="primary"
              />
              <ConfirmationDialog
                open={confirmBackOpen}
                onCancel={() => setConfirmBackOpen(false)}
                onConfirm={() => navigate(-1)}
                title="Discard Changes?"
                description="Are you sure you want to go back? Any unsaved changes will be lost."
                confirmText="Yes, Go Back"
                confirmColor="warning"
              />
            </>
          );
        }}
      </Formik>
    </Box>
  );
};

export default TourPackageForm;
